import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import gql from 'graphql-tag';
import get from 'lodash/get';
import { Modal, Button, Alert } from '@storable/canaveral-components';
import { object, string } from 'yup';
import cx from 'classnames';
import FormSubmitButton from '../FormSubmitButton';
import FormFieldText from '../FormFieldText';
import BreadcrumbParentPageDropdown from '../../containers/BreadcrumbParentPageDropdown';
import ConfirmBreadcrumbSave from '../ConfirmBreadcrumbSave/ConfirmBreadcrumbSave';
import withForm from '../../hocs/withForm';
import formatInitialValues from '../../lib/formatInitialValues';
import { DEFAULT_LOCALE } from '../../../modules/i18n/locales';
import { isHomePage, isBlogCategoryPage, isBlogPage } from '../../../modules/types';
import Icon from '../../../modules/react/components/stateless/Icon';
import BreadcrumbTrail from './BreadcrumbTrail';
import styles from './styles.scss';

const propTypes = {
  show: PropTypes.bool.isRequired,
  onDismiss: PropTypes.func.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  page: PropTypes.shape({
    id: PropTypes.string.isRequired,
    title: PropTypes.string,
    path: PropTypes.string.isRequired,
    slug: PropTypes.string,
    pageType: PropTypes.shape({
      code: PropTypes.string.isRequired,
    }).isRequired,
    breadcrumbTrail: PropTypes.shape({
      name: PropTypes.string,
      descendantPages: PropTypes.arrayOf(
        PropTypes.shape({
          id: PropTypes.string.isRequired,
        }),
      ),
      parentPage: PropTypes.shape({
        id: PropTypes.string,
        internalName: PropTypes.string,
        title: PropTypes.string,
        breadcrumbTrail: PropTypes.shape({
          name: PropTypes.string,
          ancestorPages: PropTypes.arrayOf(PropTypes.shape({
            id: PropTypes.string.isRequired,
            breadcrumbTrail: PropTypes.shape({
              id: PropTypes.string,
              name: PropTypes.string,
            }),
          })),
        }),
      }),
    }),
  }).isRequired,
  site: PropTypes.shap({
    siteUrl: PropTypes.string.isRequired,
  }).isRequired,
  values: PropTypes.shape({
    slug: PropTypes.string,
    breadcrumbTrail: PropTypes.shape({
      name: PropTypes.string,
      parentPage: PropTypes.shape({
        id: PropTypes.string,
        internalName: PropTypes.string,
        title: PropTypes.string,
        breadcrumbTrail: PropTypes.shape({
          name: PropTypes.string,
          ancestorPages: PropTypes.arrayOf(PropTypes.shape({
            id: PropTypes.string.isRequired,
            breadcrumbTrail: PropTypes.shape({
              id: PropTypes.string,
              name: PropTypes.string,
            }),
          })),
        }),
      }),
    }),
  }).isRequired,
  loading: PropTypes.bool.isRequired,
  submitErrors: PropTypes.arrayOf(
    PropTypes.shape({
      code: PropTypes.number.isRequired,
      message: PropTypes.string.isRequired,
    }).isRequired,
  ),
  errors: PropTypes.shape({
    breadcrumbTrail: PropTypes.shape({
      name: PropTypes.string,
      parentPage: PropTypes.shape({
        id: PropTypes.string,
      }),
    }),
    slug: PropTypes.string,
  }),
  dirty: PropTypes.bool.isRequired,
  homePages: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    internalName: PropTypes.string,
    title: PropTypes.string,
    pageType: PropTypes.shape({
      code: PropTypes.string.isRequired,
    }).isRequired,
    locale: PropTypes.string,
  })).isRequired,
  domain: PropTypes.string.isRequired,
};

const defaultProps = {
  submitErrors: undefined,
  errors: undefined,
};

const initialValues = {
  slug: '',
  breadcrumbTrail: undefined,
};

const BreadcrumbModal = ({
  show,
  onDismiss,
  handleSubmit,
  page,
  site,
  values,
  loading,
  submitErrors,
  errors: formErrors,
  dirty,
  homePages,
  domain,
}) => {
  const { siteUrl } = site;

  const {
    id: pageId,
    title: pageTitle,
    slug,
    locale,
    breadcrumbTrail,
  } = page;

  const [showConfirm, setConfirming] = useState(false);

  useEffect(() => {
    if (submitErrors) { setConfirming(false); }
  }, [submitErrors]);

  const breadcrumbName = get(values, 'breadcrumbTrail.name', pageTitle);
  const excludeIds = [pageId];

  const parentPageId = get(breadcrumbTrail, 'parentPage.id');
  /* Given a page's breadcrumb trail, if a breadcrumb has a null path
   * its implied parent page is the home page.
   * Since we never save the home page to a breadcrumbTrail path, a breadcrumbTrail's parentPage
   * is oftentimes null.
   * However, passing [null, ...someTruthyIds] will break the useSearchablePages hook
   * used in the BreadcrumbParentPageDropdown component
   */
  if (parentPageId) {
    excludeIds.push(parentPageId);
  }

  const descendants = get(values, 'breadcrumbTrail.descendantPages', []);
  if (descendants && descendants.length) {
    excludeIds.push(...descendants.map(desc => desc.id));
  }

  const onClose = () => {
    if (showConfirm) {
      setConfirming(false);
    } else {
      onDismiss();
    }
  };

  const onSubmit = (e) => {
    e.preventDefault();

    if (!showConfirm) {
      setConfirming(true);
    } else {
      handleSubmit();
    }
  };

  const parentPage = get(
    breadcrumbTrail,
    'parentPage',
  ) || homePages.find(homePage => homePage.locale === locale);

  const inProgressBreadcrumbTrail = get(values, 'breadcrumbTrail', { parentPage });
  const containsDrafts = !page.live || !get(inProgressBreadcrumbTrail, 'parentPage.live', true)
    || get(inProgressBreadcrumbTrail, 'parentPage.breadcrumbTrail.ancestorPages', [])
      .some(ancestor => !ancestor.live);

  const domainToUse = domain || `${siteUrl}/`;
  const baseUrl = `${domainToUse}${locale === DEFAULT_LOCALE ? '' : `${locale}/`}`;

  const parentSlug = inProgressBreadcrumbTrail.parentPage
    && !isHomePage(inProgressBreadcrumbTrail.parentPage.pageType.code)
    ? `${inProgressBreadcrumbTrail.parentPage.slug}/` : '';

  return (
    <Modal
      show={show}
      onDismiss={onDismiss}
      size="xl"
    >
      <form onSubmit={onSubmit}>
        <Modal.Header>
          <Modal.Title id="breadcrumb-modal">
            Edit Breadcrumbs and Custom URL
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {submitErrors && !showConfirm && (
            <Alert context="danger">
              {submitErrors.map(({ message }) => message)}
            </Alert>
          )}
          {showConfirm ? (
            <ConfirmBreadcrumbSave
              page={page}
            />
          ) : (
            <div>
              <div className={cx(styles.section, styles.flex)}>
                <BreadcrumbParentPageDropdown
                  name="breadcrumbTrail.parentPage"
                  label="Parent Page"
                  locale={locale}
                  parentPage={parentPage}
                  excludeIds={excludeIds}
                />
              </div>
              {containsDrafts && (
                <Alert context="info" className={styles.alert}>
                  <Icon id="info" label="draft warning" className={styles.infoIcon} />
                  <div className={styles.infoText}>
                    This breadcrumb structure contains one or more drafts.  Below is a preview
                    of what will display when all pages in the breadcrumbs/path are live.
                  </div>
                </Alert>
              )}
              <div className={styles.section}>
                <div className={styles.header}>
                  Breadcrumbs
                </div>
                <div className={styles.marginBottom}>
                  Breadcrumb links will appear if a Breadcrumbs widget is placed on the page.
                  The structure is built from the Parent Page selected above.
                </div>
                <div className={styles.preview}>
                  <BreadcrumbTrail
                    page={page}
                    site={site}
                    breadcrumbTrail={inProgressBreadcrumbTrail}
                  />
                </div>
                <div>
                  <FormFieldText
                    className={styles.input}
                    label="Breadcrumb Title"
                    name="breadcrumbTrail.name"
                    placeholder={breadcrumbName}
                  />
                </div>
              </div>
              <div className={styles.header}>
                Custom URL
              </div>
              <div className={styles.marginBottom}>
                Modify your URL by creating a custom slug for this page.
                The structure is built from the Parent Page selected above.
              </div>
              <div className={styles.preview}>
                {`${baseUrl}${parentSlug}${values.slug}`}
              </div>
              <div>
                <FormFieldText
                  className={styles.input}
                  label="Slug"
                  name="slug"
                  placeholder={slug}
                />
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            context="neutral"
            onClick={onClose}
            size="sm"
            disabled={loading}
          >
            {showConfirm ? 'Cancel' : 'Close'}
          </Button>
          <FormSubmitButton
            data-testid="breadcrumb-trail-submit"
            size="sm"
            disabled={Object.keys(formErrors).length > 0 || !dirty}
            submitting={loading}
          />
        </Modal.Footer>
      </form>
    </Modal>
  );
};

BreadcrumbModal.propTypes = propTypes;
BreadcrumbModal.defaultProps = defaultProps;

BreadcrumbModal.fragments = {
  site: gql`
    fragment BreadcrumbModalSite on Site {
      siteUrl
      customCompanyName
    }
  `,
  page: gql`
    fragment BreadcrumbModalPage on Page {
      path
      slug
      title
      locale
      live
      pageType {
        code
      }
      breadcrumbTrail {
        id
        name
        descendantPages {
          id
          live
        }
        parentPage {
          id
          internalName
          title
          live
          pageType {
            code
          }
          slug
          breadcrumbTrail {
            id
            name
            ancestorPages {
              id
              live
              breadcrumbTrail {
                id
                name
              }
            }
          }
        }
      }
    }
  `,
  homePages: gql`
    fragment BreadcrumbModalHomePages on PagesConnection {
      edges {
        node {
          id
          title
          internalName
          locale
          pageType {
            code
          }
          live
        }
      }
      totalCount
    }
  `,
};

const validationSchema = ({ page: { pageType: { code } } }) => (
  object().shape({
    breadcrumbTrail: object().shape({
      parentPage: object().shape({
        id: string().required('Parent Page is required'),
      }).test('blog-parent-only-homepage', 'Blog pages may not have a parent other than the home page',
        ({ pageType: { code: parentCode } }) => (
          (isBlogPage(code) || isBlogCategoryPage(code)) ? isHomePage(parentCode) : true
        )),
      name: string().required('Breadcrumb Title is required'),
    }),
    slug: string().required()
      .test('is-valid-slug', 'Only a-z, numbers, "/", "_" and "-" allowed', slug => /^[a-z0-9/_-]*$/.test(slug))
      .test('invalid-trailing-character', 'Slug may not end with a slash', slug => !/.*\/$/.test(slug))
      .test('no-leading-slashes', 'Slug may not begin with a slash', slug => !/^\//.test(slug))
      .test('blog-slug', 'Blog posts must start with "blog/"',
        slug => !(isBlogPage(code) && slug.slice(0, 5) !== 'blog/'))
      .test('blog-category-slug', 'Blog categories must start with "blog/category/"',
        slug => !(isBlogCategoryPage(code) && slug.slice(0, 14) !== 'blog/category/')),
  }));

export default withForm({
  enableReinitialize: true,
  mapPropsToValues: (props) => {
    const { page } = props;
    const parentSlug = get(page, 'breadcrumbTrail.parentPage.slug');

    if (parentSlug) {
      page.slug = page.slug.replace(`${parentSlug}/`, '');
    }

    return formatInitialValues(initialValues, page);
  },
  validationSchema,
  handleSubmit: ({
    breadcrumbTrail: { name, parentPage: { id, slug: parentSlug, pageType: { code } } }, slug,
  }, { props: { onSave, page: { id: pageId } } }) => {
    let fullSlug = `${parentSlug}/${slug}`;
    const breadcrumbTrail = { name, parentPageId: id };

    if (isHomePage(code)) {
      breadcrumbTrail.parentPageId = null;
      fullSlug = slug;
    }

    const page = { metadata: { slug: fullSlug } };
    onSave({ pageId, breadcrumbTrail, page });
  },
})(BreadcrumbModal);
